import { useMemo } from 'react';
import { create, all } from 'mathjs';

const math = create(all);

export interface SeriesConfig {
  expr: string;
  label?: string;
  stroke?: string;
  gradientId?: string;
}

export interface IntersectionPoint {
  x: number;
  y: number;
}

interface PlotChartProps {
  /* single-expression convenience */
  expression?: string;
  /* multi-series */
  series?: SeriesConfig[];
  variable?: string;
  xMin?: number;
  xMax?: number;
  intersections?: IntersectionPoint[];
  height?: number;
  className?: string;
}

const W_BASE = 560;
const H_DEFAULT = 260;
const PAD = { left: 48, right: 16, top: 20, bottom: 36 };

const DEFAULT_STROKES = [
  '#a78bfa', // violet
  '#34d399', // emerald
  '#fb923c', // orange
  '#f472b6', // pink
];
const DEFAULT_GRAD_STOPS: [string, string][] = [
  ['#a78bfa', '#22d3ee'],
  ['#34d399', '#059669'],
  ['#fb923c', '#f59e0b'],
  ['#f472b6', '#ec4899'],
];

type Point = { x: number; y: number | null };

function evaluateSeries(
  expr: string,
  variable: string,
  xMin: number,
  xMax: number,
  N = 400
): { points: Point[]; ys: number[] } {
  const points: Point[] = [];
  const ys: number[] = [];
  for (let i = 0; i <= N; i++) {
    const xv = xMin + (i / N) * (xMax - xMin);
    try {
      const scope: Record<string, number> = {};
      scope[variable] = xv;
      const yv = Number(math.evaluate(expr, scope));
      if (isFinite(yv) && Math.abs(yv) < 1e6) {
        points.push({ x: xv, y: yv });
        ys.push(yv);
      } else {
        points.push({ x: xv, y: null });
      }
    } catch {
      points.push({ x: xv, y: null });
    }
  }
  return { points, ys };
}

function buildPath(
  points: Point[],
  toSvgX: (x: number) => number,
  toSvgY: (y: number) => number,
  plotH: number
): string {
  const segments: string[] = [];
  let pen: 'up' | 'down' = 'up';
  let d = '';

  for (let i = 0; i < points.length; i++) {
    const pt = points[i];
    if (pt.y === null) {
      if (d) segments.push(d);
      d = '';
      pen = 'up';
    } else {
      const svgX = toSvgX(pt.x);
      const svgY = toSvgY(pt.y);
      if (pen === 'down' && i > 0 && points[i - 1].y !== null) {
        const prevY = toSvgY(points[i - 1].y as number);
        if (Math.abs(svgY - prevY) > plotH * 0.6) {
          segments.push(d);
          d = `M ${svgX} ${svgY}`;
          pen = 'down';
          continue;
        }
      }
      if (pen === 'up') {
        d = `M ${svgX} ${svgY}`;
        pen = 'down';
      } else {
        d += ` L ${svgX} ${svgY}`;
      }
    }
  }
  if (d) segments.push(d);
  return segments.join(' ');
}

export default function PlotChart({
  expression,
  series,
  variable = 'x',
  xMin = -5,
  xMax = 5,
  intersections,
  height = H_DEFAULT,
  className = '',
}: PlotChartProps) {
  // Normalise to series array
  const allSeries: SeriesConfig[] = useMemo(() => {
    if (series && series.length > 0) return series;
    if (expression) return [{ expr: expression }];
    return [];
  }, [expression, series]);

  const plotW = W_BASE - PAD.left - PAD.right;
  const plotH = height - PAD.top - PAD.bottom;

  // Evaluate all series
  const evaluated = useMemo(
    () => allSeries.map(s => evaluateSeries(s.expr, variable, xMin, xMax)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(allSeries), variable, xMin, xMax]
  );

  // Combined y range
  const { yMin, yMax } = useMemo(() => {
    const allYs = evaluated.flatMap(e => e.ys);
    if (allYs.length === 0) return { yMin: -1, yMax: 1 };
    const raw = { yMin: Math.min(...allYs), yMax: Math.max(...allYs) };
    const pad = (raw.yMax - raw.yMin) * 0.12 || 1;
    return { yMin: raw.yMin - pad, yMax: raw.yMax + pad };
  }, [evaluated]);

  const toSvgX = (x: number) => PAD.left + ((x - xMin) / (xMax - xMin)) * plotW;
  const toSvgY = (y: number) => PAD.top + (1 - (y - yMin) / (yMax - yMin)) * plotH;

  // Build paths
  const paths = useMemo(
    () => evaluated.map(e => buildPath(e.points, toSvgX, toSvgY, plotH)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [evaluated, yMin, yMax, xMin, xMax]
  );

  // Grid ticks
  const xTicks = useMemo(() => {
    const step = Math.pow(10, Math.floor(Math.log10((xMax - xMin) / 5)));
    const ticks: number[] = [];
    const start = Math.ceil(xMin / step) * step;
    for (let v = start; v <= xMax + 1e-10; v += step) ticks.push(Math.round(v * 1e8) / 1e8);
    return ticks.slice(0, 12);
  }, [xMin, xMax]);

  const yTicks = useMemo(() => {
    const step = Math.pow(10, Math.floor(Math.log10((yMax - yMin) / 5)));
    const ticks: number[] = [];
    const start = Math.ceil(yMin / step) * step;
    for (let v = start; v <= yMax + 1e-10; v += step) ticks.push(Math.round(v * 1e8) / 1e8);
    return ticks.slice(0, 10);
  }, [yMin, yMax]);

  const axisX = toSvgX(0);
  const axisY = toSvgY(0);
  const clampedAxisX = Math.max(PAD.left, Math.min(PAD.left + plotW, axisX));
  const clampedAxisY = Math.max(PAD.top, Math.min(PAD.top + plotH, axisY));

  const uid = useMemo(() => `plg-${Math.random().toString(36).slice(2)}`, []);

  return (
    <div className={`rounded-xl overflow-hidden bg-gray-950 ${className}`}>
      <svg
        viewBox={`0 0 ${W_BASE} ${height}`}
        width="100%"
        style={{ display: 'block' }}
        aria-label="Function plot"
      >
        <defs>
          {allSeries.map((s, i) => {
            const [c1, c2] = DEFAULT_GRAD_STOPS[i % DEFAULT_GRAD_STOPS.length];
            const gid = s.gradientId ?? `${uid}-g${i}`;
            return (
              <linearGradient key={gid} id={gid} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={c1} />
                <stop offset="100%" stopColor={c2} />
              </linearGradient>
            );
          })}
          <clipPath id={`${uid}-clip`}>
            <rect x={PAD.left} y={PAD.top} width={plotW} height={plotH} />
          </clipPath>
        </defs>

        {/* Background */}
        <rect x={PAD.left} y={PAD.top} width={plotW} height={plotH} fill="#0f172a" rx="6" />

        {/* Grid */}
        {xTicks.map(xv => {
          const sx = toSvgX(xv);
          return sx >= PAD.left && sx <= PAD.left + plotW
            ? <line key={`gx${xv}`} x1={sx} y1={PAD.top} x2={sx} y2={PAD.top + plotH} stroke="#1e293b" strokeWidth="1" />
            : null;
        })}
        {yTicks.map(yv => {
          const sy = toSvgY(yv);
          return sy >= PAD.top && sy <= PAD.top + plotH
            ? <line key={`gy${yv}`} x1={PAD.left} y1={sy} x2={PAD.left + plotW} y2={sy} stroke="#1e293b" strokeWidth="1" />
            : null;
        })}

        {/* Axes */}
        <line x1={clampedAxisX} y1={PAD.top} x2={clampedAxisX} y2={PAD.top + plotH} stroke="#475569" strokeWidth="1.5" />
        <line x1={PAD.left} y1={clampedAxisY} x2={PAD.left + plotW} y2={clampedAxisY} stroke="#475569" strokeWidth="1.5" />

        {/* Tick labels */}
        {xTicks.map(xv => {
          const sx = toSvgX(xv);
          if (sx < PAD.left + 2 || sx > PAD.left + plotW - 2) return null;
          return <text key={`lx${xv}`} x={sx} y={Math.min(PAD.top + plotH + 14, height - 4)} textAnchor="middle" fontSize="9" fill="#64748b" fontFamily="monospace">{xv}</text>;
        })}
        {yTicks.map(yv => {
          const sy = toSvgY(yv);
          if (sy < PAD.top + 2 || sy > PAD.top + plotH - 2) return null;
          return <text key={`ly${yv}`} x={PAD.left - 4} y={sy + 3} textAnchor="end" fontSize="9" fill="#64748b" fontFamily="monospace">{yv}</text>;
        })}

        {/* Origin dot */}
        {axisX >= PAD.left && axisX <= PAD.left + plotW && axisY >= PAD.top && axisY <= PAD.top + plotH && (
          <circle cx={clampedAxisX} cy={clampedAxisY} r="2.5" fill="#475569" />
        )}

        {/* Curves */}
        {allSeries.map((s, i) => {
          const gid = s.gradientId ?? `${uid}-g${i}`;
          const strokeColor = s.stroke ?? `url(#${gid})`;
          return (
            <path
              key={i}
              d={paths[i] ?? ''}
              fill="none"
              stroke={strokeColor}
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              clipPath={`url(#${uid}-clip)`}
            />
          );
        })}

        {/* Intersection markers */}
        {intersections?.map((pt, i) => {
          const sx = toSvgX(pt.x);
          const sy = toSvgY(pt.y);
          if (sx < PAD.left || sx > PAD.left + plotW || sy < PAD.top || sy > PAD.top + plotH) return null;
          return (
            <g key={i} clipPath={`url(#${uid}-clip)`}>
              {/* Outer ring */}
              <circle cx={sx} cy={sy} r="7" fill="none" stroke="#f97316" strokeWidth="1.5" opacity="0.6" />
              {/* Inner dot */}
              <circle cx={sx} cy={sy} r="3.5" fill="#f97316" />
              {/* Coordinate label */}
              <text
                x={sx + 8}
                y={sy - 6}
                fontSize="8"
                fill="#f97316"
                fontFamily="monospace"
                fontWeight="600"
              >
                ({Math.round(pt.x * 100) / 100}, {Math.round(pt.y * 100) / 100})
              </text>
            </g>
          );
        })}

        {/* Legend (top-left) */}
        {allSeries.map((s, i) => {
          const [c1] = DEFAULT_GRAD_STOPS[i % DEFAULT_GRAD_STOPS.length];
          const label = s.label ?? s.expr;
          const display = label.length > 22 ? label.slice(0, 22) + '…' : label;
          return (
            <g key={`leg${i}`}>
              <rect x={PAD.left + 5} y={PAD.top + 5 + i * 14} width="10" height="3" rx="1.5" fill={c1} />
              <text x={PAD.left + 19} y={PAD.top + 11 + i * 14} fontSize="9" fill={c1} fontFamily="monospace" fontWeight="600">
                y = {display}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
